import { AxiosRequestConfig } from 'axios';
import api from './client';

// Organization API
export const organizationAPI = {
  create: (
    name: string,
    options?: {
      description?: string;
      allowJoinRequests?: boolean;
      isPublic?: boolean;
      isPersonal?: boolean;
      // Discovery fields (Spec 002)
      category?: string;
      tagline?: string;
      tags?: string[];
      iconUrl?: string;
      location?: string;
      locationType?: 'online' | 'in_person' | 'hybrid';
      joinType?: 'open' | 'request' | 'invite';
    }
  ) => api.post('/organizations', { name, ...options }),

  getAll: () => api.get('/organizations'),

  getDetails: (organizationId: string) =>
    api.get(`/organizations/${organizationId}`),

  getMembers: (organizationId: string, config?: AxiosRequestConfig) =>
    api.get(`/organizations/${organizationId}/members`, config),

  addMember: (organizationId: string, email: string, role?: string) =>
    api.post(`/organizations/${organizationId}/members`, { email, role }),

  updateMemberRole: (organizationId: string, memberId: string, role: string) =>
    api.put(`/organizations/${organizationId}/members/${memberId}`, { role }),

  updateMemberDisplayName: (organizationId: string, memberId: string, displayName: string) =>
    api.put(`/organizations/${organizationId}/members/${memberId}/display-name`, { displayName }),

  removeMember: (organizationId: string, memberId: string) =>
    api.delete(`/organizations/${organizationId}/members/${memberId}`),

  // Phase 0: Invite management
  createInvite: (
    organizationId: string,
    data: {
      email?: string;
      role?: string;
      expiresInDays?: number;
      maxUses?: number;
    }
  ) => api.post(`/organizations/${organizationId}/invites`, data),

  getInviteDetails: (inviteCode: string) =>
    api.get(`/organizations/invites/${inviteCode}`),

  acceptInvite: (inviteCode: string) =>
    api.post(`/organizations/invites/${inviteCode}/accept`),

  // Phase 3.1: Email invitations
  sendInvitation: (
    organizationId: string,
    data: {
      email: string;
      // Matches backend VALID_MEMBER_ROLES (backend/src/services/authorizationService.ts) —
      // widened from 'member' | 'admin' so helm/submissive/owner grants aren't blocked
      // client-side now that invitationController.ts enforces the same canonical guard.
      role?: 'member' | 'admin' | 'submissive' | 'owner';
    }
  ) => api.post(`/organizations/${organizationId}/invitations`, data),

  getInvitations: (organizationId: string, status?: 'pending' | 'accepted' | 'expired' | 'revoked') =>
    api.get(`/organizations/${organizationId}/invitations`, { params: { status } }),

  resendInvitation: (organizationId: string, invitationId: string) =>
    api.post(`/organizations/${organizationId}/invitations/${invitationId}/resend`),

  revokeInvitation: (organizationId: string, invitationId: string) =>
    api.delete(`/organizations/${organizationId}/invitations/${invitationId}`),

  // Phase 0: Join requests
  createJoinRequest: (organizationId: string, message: string) =>
    api.post(`/organizations/${organizationId}/join-requests`, { message }),

  getJoinRequests: (organizationId: string, status?: string) =>
    api.get(`/organizations/${organizationId}/join-requests`, { params: { status } }),

  reviewJoinRequest: (
    requestId: string,
    action: 'approved' | 'rejected',
    responseMessage?: string
  ) => api.put(`/organizations/join-requests/${requestId}`, { action, responseMessage }),

  // Phase 0: Public organizations with rich filtering
  getPublicOrganizations: (params?: {
    search?: string;
    category?: string;
    locationType?: string;
    tag?: string;
    sort?: 'activity' | 'members' | 'newest' | 'name';
  }, options?: { signal?: AbortSignal }) =>
    api.get('/organizations/public', {
      params: {
        search: params?.search,
        category: params?.category,
        location_type: params?.locationType,
        tag: params?.tag,
        sort: params?.sort,
      },
      signal: options?.signal,
    }),

  // Public categories for filter dropdown
  getPublicCategories: () => api.get('/organizations/categories'),

  // Organization settings
  // Self-service leave
  leaveOrganization: (organizationId: string, targetOrganizationId?: string) =>
    api.post(`/organizations/${organizationId}/leave`, { targetOrganizationId }),

  getSettings: (organizationId: string) =>
    api.get(`/organizations/${organizationId}/settings`),

  updateSettings: (
    organizationId: string,
    settings: {
      defaultTaskSort?: 'due_date' | 'priority';
      hideCompletedTasks?: boolean;
      autoArchiveEnabled?: boolean;
      autoArchiveAfterDays?: number;
      archiveSchedule?: 'daily' | 'weekly_sunday' | 'weekly_monday';
      requireReviewDefault?: boolean;
      dependencyUnblockStatus?: 'submitted' | 'approved' | 'completed';
      // Spec 241: grace period (days, 0-90) an optional task stays completable
      // past due date before it expires. Evaluated live — applies to existing
      // optional tasks too.
      optionalTaskGraceDays?: number;
      /** Lives on organization_settings (baseline-sql/058), read by praiseService
       *  when awarding a praise bonus — NOT a point_config column. */
      praisePointsBonus?: number;
      timezone?: string;
      weekStartsOn?: 0 | 1;
      defaultDueTime?: string;
      observeDst?: boolean;
      // Task creation defaults
      defaultPriority?: 'low' | 'medium' | 'high' | null;
      defaultPrivate?: boolean;
      defaultScheduleType?: 'one_time' | 'daily' | 'weekly' | 'monthly';
      // Spec 095 — Hierarchy task defaults
      defaultAssigneeIds?: string[];
      defaultCompletionPoints?: number | null;
      // Spec 179 — Proof timestamp enforcement
      defaultProofEnforcementMode?: 'off' | 'warn' | 'strict';
      defaultProofWindowHours?: 1 | 4 | 24 | null;
      // Reward defaults
      defaultRewardViewMode?: 'view_once' | 'view_24hr' | 'downloadable';
      defaultRewardRevealType?: boolean;
      // Organization visibility settings
      isPublic?: boolean;
      allowJoinRequests?: boolean;
      // Discovery fields (Spec 002)
      category?: string | null;
      tagline?: string | null;
      tags?: string[];
      iconUrl?: string | null;
      location?: string | null;
      locationType?: 'online' | 'in_person' | 'hybrid' | null;
      joinType?: 'open' | 'request' | 'invite';
      // Spec 113 — Dynamic-specific terminology
      terminology?: Record<string, string>;
    }
  ) => api.put(`/organizations/${organizationId}/settings`, settings),

  // Phase 3.2.2: Multi-org invite status
  listWithInviteStatus: () => api.get('/organizations/with-invite-status'),

  // Organization Health Dashboard
  getHealthDashboard: (organizationId: string) =>
    api.get(`/organizations/${organizationId}/health`),

  getHealthSummary: (organizationId: string) =>
    api.get(`/organizations/${organizationId}/health/summary`),

  // Aggregated context endpoints (reduces API calls)
  /**
   * Get complete organization context in a single call.
   * Replaces: getMembers() + getSettings() + getBillingStatus()
   */
  getContext: (organizationId: string) =>
    api.get(`/organizations/${organizationId}/context`),

  /**
   * Get lightweight members-only context for modals.
   * Use for QuickPraiseModal, SendRewardModal, etc.
   */
  getMembersOnly: (organizationId: string) =>
    api.get(`/organizations/${organizationId}/members-only`),

  // Keyholder assignments (hierarchy management)
  getKeyholderAssignments: (organizationId: string) =>
    api.get(`/organizations/${organizationId}/keyholder-assignments`),

  getKeyholderAssignmentsForUser: (
    organizationId: string,
    userId: string,
    role?: 'submissive' | 'keyholder'
  ) =>
    api.get(`/organizations/${organizationId}/keyholder-assignments/for-user/${userId}`, {
      params: { role },
    }),

  createKeyholderAssignment: (
    organizationId: string,
    data: {
      submissive_user_id: string;
      keyholder_user_id: string;
      notes?: string;
      visible_to_org?: boolean;
    }
  ) => api.post(`/organizations/${organizationId}/keyholder-assignments`, data),

  updateKeyholderAssignment: (
    organizationId: string,
    assignmentId: string,
    // Spec 329 S3: is_active is not editable (the server answers 400 USE_LINK_TRANSITION); end a link with deleteKeyholderAssignment.
    data: { notes?: string; visible_to_org?: boolean }
  ) => api.patch(`/organizations/${organizationId}/keyholder-assignments/${assignmentId}`, data),

  deleteKeyholderAssignment: (organizationId: string, assignmentId: string) =>
    api.delete(`/organizations/${organizationId}/keyholder-assignments/${assignmentId}`),

  // Motivation notes (Spec 092-B — dom daily notes to subs)
  getMotivationNotes: (orgId: string) =>
    api.get(`/organizations/${orgId}/motivation-note`),

  setMotivationNote: (orgId: string, message: string, recipientId?: string) =>
    api.put(`/organizations/${orgId}/motivation-note`, { message, recipientId }),

  deleteMotivationNote: (orgId: string, recipientId?: string) =>
    api.delete(`/organizations/${orgId}/motivation-note`, { data: { recipientId } }),

  // Limits review reminders (Spec 107)
  getLimitsReviewStatus: (orgId: string) =>
    api.get(`/organizations/${orgId}/limits-review-status`),

  markLimitsReviewed: (orgId: string) =>
    api.post(`/organizations/${orgId}/limits-reviewed`),

  updateLimitsReviewInterval: (orgId: string, intervalDays: number) =>
    api.put(`/organizations/${orgId}/limits-review-interval`, { intervalDays }),

  // Hierarchy Setup Checklist (Spec 272) — dashboard nudge for untouched free-tier hierarchies
  getSetupChecklist: (orgId: string) =>
    api.get(`/organizations/${orgId}/setup-checklist`),

  dismissSetupChecklist: (orgId: string) =>
    api.post(`/organizations/${orgId}/setup-checklist/dismiss`),

  // Collaring stages / relationship milestones (Spec 108)
  getCurrentStages: (orgId: string) =>
    api.get(`/organizations/${orgId}/stages/current`),

  getStageHistory: (orgId: string) =>
    api.get(`/organizations/${orgId}/stages/history`),

  advanceStage: (
    orgId: string,
    data: {
      submissiveId: string;
      stage: string;
      ceremonyDate?: string;
      ceremonyNotes?: string;
      customStageName?: string;
    }
  ) => api.post(`/organizations/${orgId}/stages/advance`, data),

  revertStage: (
    orgId: string,
    data: { submissiveId: string; reason: string }
  ) => api.post(`/organizations/${orgId}/stages/revert`, data),

  // Switch Role Support (Spec 112)
  getDynamicRole: (orgId: string, memberId: string) =>
    api.get(`/organizations/${orgId}/members/${memberId}/dynamic-role`),

  setDynamicRole: (
    orgId: string,
    memberId: string,
    dynamicRole: 'dominant' | 'submissive' | 'switch' | null
  ) =>
    api.put(`/organizations/${orgId}/members/${memberId}/dynamic-role`, { dynamicRole }),

  toggleMyRole: (orgId: string) =>
    api.post(`/organizations/${orgId}/my-role/toggle`),
};

// Timeline API (Spec 103 — Relationship Timeline)
export const timelineAPI = {
  getTimeline: (orgId: string, params?: {
    since?: string;
    until?: string;
    types?: string;
    limit?: number;
    offset?: number;
  }) => api.get(`/organizations/${orgId}/timeline`, { params }),

  addCustomEvent: (orgId: string, data: {
    title: string;
    description?: string;
    eventDate: string;
    icon?: string;
    color?: string;
  }) => api.post(`/organizations/${orgId}/timeline`, data),

  deleteEvent: (orgId: string, eventId: string) =>
    api.delete(`/organizations/${orgId}/timeline/${eventId}`),

  getDuration: (orgId: string) =>
    api.get(`/organizations/${orgId}/timeline/duration`),
};

// Billing API (Organization-level)
export const billingAPI = {
  // Get billing status for organization
  getStatus: () => api.get('/billing/status'),

  // Get available subscription plans
  getPlans: () => api.get('/billing/plans'),

  // Get storage breakdown (admin only)
  getStorageBreakdown: () => api.get('/billing/storage/breakdown'),

  // Check if upload is allowed
  checkUpload: (fileSize: number) =>
    api.post('/billing/storage/check-upload', { fileSize }),

  // Recalculate storage (admin only)
  recalculateStorage: () => api.post('/billing/storage/recalculate'),

  // Create subscription intent for Payment Element (theme-aware checkout)
  createSubscription: (data: { planSlug: string }) =>
    api.post('/billing/subscribe', data),

  // Create Stripe checkout session (embedded checkout fallback)
  createCheckout: (data: { planSlug: string; successUrl: string; cancelUrl: string }) =>
    api.post('/billing/checkout', data),

  // Create Stripe customer portal session
  createPortal: (data: { returnUrl: string }) =>
    api.post('/billing/portal', data),

  // Cancel subscription
  cancel: (data: { immediately?: boolean }) =>
    api.post('/billing/cancel', data),

  // Reactivate subscription
  reactivate: () => api.post('/billing/reactivate'),
};
